import React, { useCallback, useEffect, useState } from 'react';

import { ConditionalForm, ConditionalInput } from '@travpro/components/cjs';
import { useClassName } from '@travpro/hooks';
import i18n from 'i18n-react';
import { Redirect } from 'react-router';
import { toast } from 'react-toastify';
import { HashLink as Link } from 'react-router-hash-link';

import APIHandler from 'config/APIHandler';
import settings from 'config/settings';
import StorageHandler from 'config/StorageHandler';

import stateMap from './state_map';

/** @enum {string} */
const MemberTypes = {
  skiArea: 'skiArea',
  supplier: 'supplier',
  other: 'other',
};

/**
 * @param {Function} t Getter function for the `useTranslate` hook, defaults to returning translation key
 * @param {object} data Extra data to construct fields with
 * @returns {Object<string, import('@travpro/components/ConditionalInput').ConditionalInputProps>} Input object for
 *   conditional form
 */
const formTemplate = (t, data) => ({
  memberType: {
    label: true,
    options: Object.values(MemberTypes).map((value) => ({ label: t(value), value })),
    type: 'select',
  },
  memberState: {
    label: t('memberState'),
    inputLabel: t('memberStateInput'),
    options: Object.keys(data.skiAreas).map((value) => ({ label: stateMap[value] || value, value })),
    type: 'select',
    where: ({ memberType }) => memberType === MemberTypes.skiArea,
  },
  skiAreas: {
    label: t('skiAreas'),
    inputLael: t('skiAreas'),
    options: ({ memberState }) =>
      (data.skiAreas[memberState] || []).map((label) => ({
        label,
        value: `${memberState}_${label}`,
      })),
    type: 'select',
    where: ({ memberState }) => memberState !== undefined,
  },
});

const where = ({ memberType, ...formData }) => {
  const conditions = [
    memberType === MemberTypes.other,
    memberType === MemberTypes.skiArea && formData.memberState && formData.skiAreas !== undefined,
    memberType === MemberTypes.supplier,
  ];
  return conditions.some((condition) => condition === true);
};

const registerFormTemplate = (t = (key) => key) => ({
  company: {
    label: t('company'),
    type: 'text',
    required: true,
    where: ({ memberType }) => memberType && memberType !== MemberTypes.skiArea,
  },
  ...['first_name', 'last_name'].reduce(
    (output, key) => ({
      ...output,
      [key]: {
        label: t(key),
        type: 'text',
        required: true,
        onInvalid: (e, setHelperText) => {
          setHelperText(t('invalid', { target: t(e.target.id) }));
        },
        where,
      },
    }),
    {},
  ),
  email: {
    label: t('email'),
    type: 'email',
    required: true,
    where,
  },
  password: {
    label: t('password'),
    type: 'password',
    required: true,
    where,
  },
  confirmPassword: {
    label: t('confirmPassword'),
    type: 'password',
    required: true,
    validate: (e, { password, confirmPassword }) =>
      (password !== undefined || confirmPassword !== undefined) && password !== confirmPassword && t('passwordNoMatch'),
    where,
  },
  jobFunction: {
    label: t('jobFunction'),
    type: 'text',
    where,
  },
});

const getSkiAreas = async (callback) =>
  callback(await (await fetch('https://crm.travpromobile.com/assets/ski_areas.json')).json());

// useTranslate doesn't work in this stack and I can't be bothered fixing it
const useTranslate = (block) => {
  const t = (key, options) => i18n.translate([block, key].join('.'), options);
  t.general = (key, options) => i18n.translate(['general', key].join('.'), options);
  t.error = (key, options) => i18n.translate(['error', key].join('.'), options);
  return t;
};

const Register = () => {
  // Legacy
  const [redirect, setRedirect] = useState(null);
  const [skiAreas, setSkiAreas] = useState({});
  const [disabled, setDisabled] = useState(false);

  const c = useClassName('register');
  const t = useTranslate('register');

  const form = formTemplate(t, { skiAreas });
  const registerForm = registerFormTemplate(t);

  const goBack = useCallback(() => {

      if (params.get('mobile')) {
        window.postMessage('back');
        window.postMessage(
          'back',
          '*',
        );
        
        window.ReactNativeWebView.postMessage('back');
        window.ReactNativeWebView.postMessage(
          'back',
          '*',
        );

        console.log('test');
      } else if (window.location.pathname === '/register-mobile') {
        window.postMessage('back');
        window.postMessage(
          'back',
          '*',
        );

        window.ReactNativeWebView.postMessage('back');
        window.ReactNativeWebView.postMessage(
          'back',
          '*',
        );

        console.log('test');
      } else {
        setRedirect('/login');
      }
    }, []
  );

  const submitForm = useCallback(async (e, formData) => {
    setDisabled(true);

    const response = await APIHandler.registerSSOUser({
      type_of_user: formData?.memberType,
      name: [formData?.first_name?.trim(), formData?.last_name.trim()]?.join(' '),
      first_name: [formData?.first_name],
      last_name: [formData?.last_name],
      email: formData?.email,
      title: formData?.jobFunction === undefined ? '' : formData.jobFunction,
      agency_name: formData?.company === undefined ? '' : formData.company,
      password: formData?.password,
      password_confirmation: formData?.confirmPassword,

      custom_1: formData?.memberState,
      custom_2: (formData?.skiAreas || '').replace(`${formData?.memberState}_`, '') || undefined,
    });

    const registerObject = {
      type_of_user: formData?.memberType,
      name: [formData?.first_name.trim(), formData?.last_name.trim()].join(' '),
      first_name: [formData?.first_name],
      last_name: [formData?.last_name],
      email: formData?.email,
      title: formData?.jobFunction === undefined ? '' : formData.jobFunction,
      agency_name: formData?.company === undefined ? '' : formData.company,
      password: formData?.password,
      password_confirmation: formData?.confirmPassword,

      custom_1: formData?.memberState,
      custom_2: (formData?.skiAreas || '').replace(`${formData?.memberState}_`, '') || undefined,
    };

    if (formData?.password.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      setDisabled(false);
      return;
    }

    if (!response.success) {
      toast.error(response.message);
      setDisabled(false);
      return;
    }
    
    await APIHandler.setUser(formData?.email, settings.baseAppId, registerObject);

    if (response.success === true) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mobile')) {
        toast.success('Registration successful. After 1 second you will be redirected to the home page.', {
          autoClose: 1000,
        });
  
        window.postMessage('back');
        window.postMessage(
          'back',
          '*',
        );

        window.ReactNativeWebView.postMessage('back');
        window.ReactNativeWebView.postMessage(
          'back',
          '*',
        );

        return;
      }

      if (window.location.pathname === '/register-mobile') {
        toast.success('Registration successful. After 1 second you will be redirected to the home page.', {
          autoClose: 1000,
        });

        window.postMessage('back');
        window.postMessage(
          'back',
          '*',
        );

        window.ReactNativeWebView.postMessage('back');
        window.ReactNativeWebView.postMessage(
          'back',
          '*',
        );
        return;
      }

      toast.success('Registration successful. After 1 second you will be redirected to the home page.', {
        autoClose: 1000,
        onClose: () => setRedirect('/main'),
      });

      response.user.first_name = formData?.first_name;
      response.user.last_name = formData?.last_name;

      await StorageHandler.save('user', response.user);
    } else {
      toast.error(response.message);
      setDisabled(false);
    }
  }, []);

  useEffect(() => {
    getSkiAreas(setSkiAreas);
  }, []);

  const params = new URLSearchParams(window.location.search);
  return (
    <div {...c()}>
      {Object.keys(skiAreas).length > 0 && (
        <ConditionalForm
          onSubmit={submitForm}
          onInvalid={() => {
            toast.error('Form contains invalid data');
          }}
        >
          {form &&
            Object.entries(form).map(([id, { label, ...props }]) => (
              <ConditionalInput key={id} id={id} label={label === true ? t(id) : label} {...props} />
            ))}

          {registerForm &&
            Object.entries(registerForm).map(([id, { label, ...props }]) => (
              <ConditionalInput key={id} id={id} label={label === true ? t(id) : label} {...props} />
            ))}

          <div {...c('footer')}>            
              <button type="button" onClick={goBack} disabled={disabled}>
                {t('back')}
              </button>
            <button type="submit" disabled={disabled} className="btnSolid">
              {t('submit')}
            </button>
          </div>
        </ConditionalForm>
      )}
      {redirect !== null && <Redirect to={redirect} />}
      {params.get('mobile') &&
        <style>{'\
        .buttonWrapper { display: none !important; } .btnInverse { display: none !important; } .cookieConsent { display: none; bottom: 4rem; } .register { display: none !important; } .loginView .loginView__bg .loginView__bg--content .buttonWrapper img { display: none !important; } .register__wrapper .btnSolid { display: block !important; }'}
        </style>
        }
        {window.location.pathname === '/register-mobile' &&
        <style>{'\
        .buttonWrapper { display: none !important; } .btnInverse { display: none !important; } .cookieConsent { display: none; bottom: 4rem; } .register { display: none !important; } .loginView .loginView__bg .loginView__bg--content .buttonWrapper img { display: none !important; } .register__wrapper .btnSolid { display: block !important; }'}
        </style>
      }
    </div>
  );
};

export default Register;

Register.propTypes = {};

Register.defaultProps = {};
